import React, { useEffect, useRef, useState } from 'react';
import { InteractionPartUtils } from '@lib/models/InteractionPart';
import { IconButton, Stack } from "@mui/material";
import { ChevronRight } from '@mui/icons-material';
import MarkdownExtended from "../../../Library/MarkdowmExtended";
import type { PartRendererProps } from './types';

const ThoughtPart: React.FC<PartRendererProps> = ({
    part,
    message,
    onGoing,
    preview,
    isLastInlineInGroup = false,
    friendlyMode = false,
    onLinkClicked,
}) => {
    // Initialize from props so the first paint already matches the
    // intended collapsed state. Otherwise the default `false` makes
    // every thought briefly render open, then animate shut once the
    // effect below runs — fine when the transition was instant
    // (`display: none`), distracting now that it animates.
    const [collapsed, setCollapsed] = useState<boolean>(() => !onGoing || !isLastInlineInGroup);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setCollapsed(!onGoing || !isLastInlineInGroup);
    }, [onGoing, isLastInlineInGroup]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo({
                top: contentRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [part.textDetails?.version]);

    if (!onGoing) return null;

    const thoughtContent = InteractionPartUtils.getPartTextContent(part);
    const ended = part.ended ?? new Date();
    const throughFor = ((ended.getTime() - part.created.getTime()) / 1000).toFixed(2);

    const thoughtComplete = isLastInlineInGroup;
    const thoughtLabel = friendlyMode
        ? (!thoughtComplete ? `Pensou por ${throughFor}s` : 'Pensando...')
        : `Thought ${throughFor}s`;

    const onDrawCodeBlock = (_content: string, _className: string) => null;

    return (
        <Stack direction={'column'} style={{ display: 'flex', borderRadius: '5px', marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
            <IconButton size="small" disableRipple onClick={() => setCollapsed(!collapsed)} sx={{
                color: 'inherit',
                padding: '1px',
                minWidth: '16px',
                fontSize: '0.75rem',
                width: friendlyMode ? '140px' : '120px',
                height: '16px',
                outline: 'none',
                marginBottom: '10px',
                '&:focus': { outline: 'none' },
                '&:focus-visible': { outline: 'none' },
                '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
            }}>
                <Stack direction={'row'} sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', alignContent: 'flex-end', flexWrap: 'nowrap' }} gap={0.5}>
                    <div style={{ minWidth: '90px' }}>{thoughtLabel}</div>
                    <ChevronRight sx={{ fontSize: '1rem', rotate: collapsed ? '0deg' : '90deg', transition: 'rotate 180ms ease-out' }} />
                </Stack>
            </IconButton>
            {/*
              * `display: none` can't be animated, so the previous
              * thought used to vanish instantly when a new one
              * arrived. Swap to a max-height + opacity + padding
              * transition: the surrounding Stack reflows along
              * with max-height, so everything below slides up
              * smoothly rather than jumping.
              */}
            <div ref={contentRef} style={{
                maxHeight: collapsed ? '0px' : '150px',
                opacity: collapsed ? 0 : 1,
                overflow: collapsed ? 'hidden' : 'scroll',
                padding: collapsed ? '0px 5px' : '2px 5px',
                fontStyle: 'italic',
                transition: 'max-height 220ms ease-out, opacity 180ms ease-out, padding 220ms ease-out',
            }}>
                <MarkdownExtended
                    partial={preview}
                    sessionId={message.sessionId ?? 'ND'}
                    text={thoughtContent ?? ""}
                    onDrawCodeBlock={onDrawCodeBlock}
                    onLinkClicked={onLinkClicked}
                />
            </div>
        </Stack>
    );
};

export default ThoughtPart;
